import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import {
  setIsEditing,
  useActivateTransportMutation,
  parseTransportVerbatimMessage,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { trackEditAirAmbulanceRequest } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { Loader } from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import { LAUNCH_TYPES } from '../AirAmbulanceForm/constants';
import {
  getErrorStatusCode,
  getSubmissionErrorMessage,
} from '../AirAmbulanceForm/utils';
import AirAmbulanceEtlCancelModal from './AirAmbulanceEtlCancelModal';

function getStatusType(
  status: EtlTransportResponse['statuses'][number],
): string | undefined {
  return status.verbatim_message?.update?.status?.type ?? undefined;
}

/**
 * Returns true when the most recent edit-location status is still pending
 * (i.e. not yet completed or disregarded by GMR).
 */
function hasUnresolvedEditLocation(
  statuses: EtlTransportResponse['statuses'],
): boolean {
  for (let i = statuses.length - 1; i >= 0; i--) {
    const type = getStatusType(statuses[i]);
    if (type === 'editLocationPending') return true;
    if (type === 'editLocationCompleted' || type === 'editLocationDisregarded')
      return false;
  }
  return false;
}

function hasCancelStarted(statuses: EtlTransportResponse['statuses']): boolean {
  return statuses.some(status => {
    const statusType = getStatusType(status);
    return (
      statusType === 'cancelPending' ||
      statusType === 'cancelled' ||
      statusType === 'cancelCompleted'
    );
  });
}

interface AirAmbulanceEtlStatusControlsProps {
  transport: EtlTransportResponse;
}

/**
 * Renders the set of action buttons on the bottom of the status page.
 */
function AirAmbulanceEtlStatusControls({
  transport,
}: AirAmbulanceEtlStatusControlsProps): JSX.Element {
  const dispatch = useDispatch();
  const [activateTransport] = useActivateTransportMutation();
  const [isActivating, setIsActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSubmittedTransportId, setCancelSubmittedTransportId] = useState<
    number | null
  >(null);

  const verbatim = parseTransportVerbatimMessage(transport.verbatim_message);
  const tripLeg = verbatim?.tripLeg?.[0];
  const isStandby = Boolean(tripLeg?.airDetails?.idenStandBy);

  const isCancelStarted =
    hasCancelStarted(transport.statuses) ||
    cancelSubmittedTransportId === transport.id;

  const isEditPending = hasUnresolvedEditLocation(transport.statuses);

  const isExternalOrder = transport.ingestion_source !== 'unite';

  const handleEditRequest = () => {
    trackEditAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
    });
    dispatch(setIsEditing(true));
  };

  const handleActivateStandby = async () => {
    setIsActivating(true);
    // Clear any error from a previous activation attempt.
    setActivateError(null);
    try {
      await activateTransport({ transportId: transport.id }).unwrap();
    } catch (error) {
      const statusCode = getErrorStatusCode(error);

      // Surface a user-facing error so the controls can render an error box.
      setActivateError(getSubmissionErrorMessage(statusCode));

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Activate standby error:', error);
    } finally {
      setIsActivating(false);
    }
  };

  const cancelledTitle = isCancelStarted
    ? 'This request has been cancelled'
    : undefined;

  const editDisabledTitle =
    cancelledTitle ?? (isEditPending ? 'Location edit pending' : undefined);

  return (
    <>
      <Box mx="8px" mb={2} pt={4}>
        {isExternalOrder ? (
          <Text size="small" color="secondaryText">
            External orders cannot be edited.
          </Text>
        ) : (
          <>
            {activateError && (
              <Box
                p={3}
                mb={1}
                bg="lightDanger"
                borderRadius="4px"
                role="alert"
              >
                <Text color="background" size="small">
                  {activateError}
                </Text>
              </Box>
            )}
            {isStandby && (
              <Box mb={1}>
                <Button
                  grow
                  btnType="primary"
                  type="button"
                  mx={0}
                  onClick={handleActivateStandby}
                  disabled={isActivating || isCancelStarted}
                  title={cancelledTitle}
                >
                  {isActivating ? (
                    <Loader />
                  ) : (
                    `Activate ${LAUNCH_TYPES.STANDBY}`
                  )}
                </Button>
              </Box>
            )}

            <Box mb={1}>
              <Button
                grow
                btnType="tertiary"
                type="button"
                mx={0}
                onClick={handleEditRequest}
                disabled={isCancelStarted || isEditPending}
                title={editDisabledTitle}
              >
                Edit Request
              </Button>
            </Box>

            <Box mb={1}>
              <Button
                grow
                btnType="warning_tertiary"
                type="button"
                mx={0}
                onClick={() => setShowCancelModal(true)}
                disabled={isCancelStarted}
                title={cancelledTitle}
              >
                Cancel Request
              </Button>
            </Box>
          </>
        )}
      </Box>

      <AirAmbulanceEtlCancelModal
        transport={transport}
        showModal={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelSubmitted={() => setCancelSubmittedTransportId(transport.id)}
      />
    </>
  );
}

export default AirAmbulanceEtlStatusControls;
